import { ExclamationCircleOutlined } from '@ant-design/icons'
import {
  type Address,
  type Carer,
  Gender,
  type Home,
  type ResidentItem,
  ResidentStatus
} from '@lmc/ts-common'
import {
  CareRelatedInformation,
  ContactDetails,
  EmptyState,
  PageHeader,
  SiteRelatedInformation
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import PersonalInformation from '@lmc/ui-kit/src/systemIcons/PersonalInformation/PersonalInformation'
import { SexEnum } from '@lmc/validations'
import { Tooltip } from 'antd'
import type { DescriptionsItemType } from 'antd/es/descriptions'
import { Spinner } from 'components/generic'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import useResident, { type ResidentItemExtended } from 'hooks/useResident'
import { useRouter } from 'hooks/useRouter'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useMemo, useRef } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import MenuGrid from 'screens/Residents/components/MenuGrid/MenuGrid'
import styled from 'styled-components'
import {
  GeneralInformation,
  getReasonableAdjustmentsViewSectionItems,
  ProfileViewSection
} from './components'
import { HospitalPassportDownloadButton } from './components/HospitalPassportDownloadButton'
import {
  ChildrenContainer,
  ExtraText,
  ProfileContent,
  ProfileWrapper
} from './styledComponents'
import { getAddressString } from './utils/getAddressString'
import {
  PERSONAL_INFO_FIELDS,
  SITE_RELATED_FIELDS
} from './utils/profileScreenFields'
import {
  getCareRelatedFields,
  getProfileViewSectionItems,
  scrollToTop
} from './utils/utils'

type ResidentProfileScreenProps = {
  testId?: string
}

export const ResidentProfileScreen = ({
  testId
}: ResidentProfileScreenProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const topRef = useRef<HTMLDivElement | null>(null)
  const {
    navigate,
    params: { id }
  } = useRouter()

  const { hasRota } = useHomeType()
  const flags = useFeatureFlags({
    flagNames: [
      'rostering_parked_features',
      'keyworkers',
      'dols_mods_alignment',
      'reasonable_adjustments',
      'mods_demographics',
      'geofencing',
      'mental_capacity'
    ]
  })?.data

  const dolsModsAlignment = flags.dols_mods_alignment?.enabled
  const reasonableAdjustmentsEnabled = flags?.reasonable_adjustments?.enabled
  const geofencingEnabled = flags?.geofencing?.enabled
  const mentalCapacityEnabled = flags?.mental_capacity?.enabled

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const { isPro, isEmar, isFromCanada, isDoLSOverride } = useHomeType()

  const isModsDemographicsEnabled = flags?.mods_demographics?.enabled

  const { resident, isLoading, updateResident, refetch, isLoadingUpdate } =
    useResident({ id })

  const DEATH_DETAILS_FIELDS = useMemo(
    () => [
      {
        key: 'deathDate',
        label: t(supConstants.DATE_OF_DEATH)
      }
    ],
    [t]
  )

  const PERSONAL_INFO_FIELDS_TO_DISPLAY = useMemo(() => {
    const isFemaleResident = isModsDemographicsEnabled
      ? (resident as any)?.demographics?.sex === SexEnum.female
      : resident?.gender === Gender.female

    const baseFields = PERSONAL_INFO_FIELDS.filter((elem) => {
      if (elem.key === 'carerGender') return flagsRosteringParkedFeaturesEnabled
      if (elem.key === 'gender') return !isModsDemographicsEnabled
      if (elem.key === 'genderIdentity' || elem.key === 'sex')
        return isModsDemographicsEnabled
      if (elem.key === 'pregnancyStatus') return isFemaleResident

      return true
    })

    if (resident?.status === ResidentStatus.deceased) {
      const dateOfBirthIndex = baseFields.findIndex(
        (field) => field.key === 'dateOfBirth'
      )
      if (dateOfBirthIndex !== -1) {
        return [
          ...baseFields.slice(0, dateOfBirthIndex + 1),
          ...DEATH_DETAILS_FIELDS,
          ...baseFields.slice(dateOfBirthIndex + 1)
        ]
      }
    }

    return baseFields
  }, [
    PERSONAL_INFO_FIELDS,
    flagsRosteringParkedFeaturesEnabled,
    isModsDemographicsEnabled,
    resident?.status,
    resident?.gender,
    (resident as any)?.demographics?.sex,
    DEATH_DETAILS_FIELDS
  ])

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const flagsKeyworkersEnabled = flags?.keyworkers?.enabled

  const keyworkersEnabled =
    flagsKeyworkersEnabled && home?.features?.goals?.enabled

  const pdfTitle = `${resident?.name?.first} ${resident?.name?.last} - Hospital Passport`

  const PDF_EXPORT_URL = `/api/list/residents/${id}/pdf/hospital-passport`

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const careRelatedFields = getCareRelatedFields(
    isPro,
    isEmar,
    isFromCanada,
    isDoLSOverride,
    hasRota || false,
    dolsModsAlignment,
    mentalCapacityEnabled
  )

  const isGeofencingEnabled =
    (hasRota ?? false) &&
    (home?.rotaSettings as { geofencing?: boolean } | undefined)?.geofencing ===
      true &&
    geofencingEnabled === true

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  if (isLoading) {
    return (
      <Spinner
        data-testid={testId ? `${testId}-loading` : 'resident-profile-loading'}
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId ? `${testId}-no-resident` : 'resident-profile-no-resident'
        }
      />
    )
  }

  const residentName = `${resident.name.first} ${resident.name.last}`

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={
        testId ? `${testId}-profile-wrapper` : 'resident-profile-wrapper'
      }
    >
      <PageHeader
        sticky
        title={`${residentName}'s ${t(supConstants.GENERAL_INFORMATION)}`}
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () => navigate(`/residents/${id}/profile`, { replace: true })
            : undefined
        }
        data-testid={
          testId ? `${testId}-page-header` : 'resident-profile-page-header'
        }
      >
        <HospitalPassportDownloadButton
          hideIcon={!isDesktop}
          isModsDemographicsEnabled={Boolean(isModsDemographicsEnabled)}
          isUpdating={isLoadingUpdate}
          pdfTitle={pdfTitle}
          refetch={refetch}
          resident={resident}
          residentId={id!}
          testId={testId}
          updateResident={updateResident}
          url={PDF_EXPORT_URL}
        />
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-profile-content` : 'resident-profile-content'
        }
      >
        <GeneralInformation resident={resident} home={home} testId={testId} />
        <MenuGrid testId={testId}>
          <ProfileViewSection
            title={t(supConstants.PERSONAL_INFORMATION)}
            icon={<PersonalInformation />}
            items={[
              ...getProfileViewSectionItems(
                PERSONAL_INFO_FIELDS_TO_DISPLAY,
                resident,
                carers,
                isFromCanada,
                keyworkersEnabled
              ),
              ...getCircumstancesViewSectionItems(resident, t)
            ]}
            action={() =>
              navigate(`../personal-information`, { relative: 'path' })
            }
            isDesktop={isDesktop}
            testId={testId}
          />
          <ProfileViewSection
            title={t(supConstants.SITE_RELATED_INFORMATION)}
            icon={<SiteRelatedInformation />}
            items={getProfileViewSectionItems(
              SITE_RELATED_FIELDS,
              resident,
              carers,
              isFromCanada
            )}
            action={() =>
              navigate(`../site-related-information`, { relative: 'path' })
            }
            isDesktop={isDesktop}
            testId={testId}
          />
          {isPro && (
            <ProfileViewSection
              title={t(supConstants.CARE_RELATED_INFORMATION)}
              icon={<CareRelatedInformation />}
              items={[
                ...getProfileViewSectionItems(
                  careRelatedFields || [],
                  resident,
                  carers,
                  isFromCanada,
                  undefined,
                  t(supConstants.NHS_NUMBER_STATUS_INFO)
                ),
                ...getCareInformationViewSectionItems(resident, t),
                ...(reasonableAdjustmentsEnabled
                  ? getReasonableAdjustmentsViewSectionItems(resident, t)
                  : []),
                ...(resident.otherIdentifiers?.map((identifier, index) => ({
                  key: `other-identifier-${index}-${identifier?.identifier}-${identifier?.type}`,
                  label: <Text>{identifier?.type}</Text>,
                  children: <Text>{identifier?.identifier}</Text>
                })) || [])
              ]}
              action={() =>
                navigate(`../care-related-information`, { relative: 'path' })
              }
              isDesktop={isDesktop}
              testId={testId}
            />
          )}
          {isPro && (
            <ProfileViewSection
              title={t(supConstants.CONTACT_DETAILS)}
              icon={<ContactDetails />}
              items={getContactDetailsViewSectionItems(
                resident,
                t,
                isGeofencingEnabled
              )}
              action={() =>
                navigate(`../contact-details`, { relative: 'path' })
              }
              isDesktop={isDesktop}
              testId={testId}
            />
          )}
        </MenuGrid>
      </ProfileContent>
    </ProfileWrapper>
  )
}

const isDesktop = isBrowser || isTablet

const getCircumstancesViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  const r = resident as ResidentItemExtended
  const candidates = [
    {
      key: 'social-circumstances',
      label: t(supConstants.SOCIAL_CIRCUMSTANCES),
      description: r.circumstances?.socialCircumstances?.description
    },
    {
      key: 'educational-history',
      label: t(supConstants.EDUCATIONAL_HISTORY),
      description: r.circumstances?.educationalHistory?.description
    },
    {
      key: 'occupational-history',
      label: t(supConstants.OCCUPATIONAL_HISTORY),
      description: r.circumstances?.occupationalHistory?.description
    }
  ]
  return candidates
    .filter((item) => item.description?.trim())
    .map((item) => ({
      key: item.key,
      label: <Text>{item.label}</Text>,
      children: <Text>{item.description}</Text>
    }))
}

const isAddressInvalidForGeofencing = (
  address: Address,
  isGeofencingEnabled: boolean
) =>
  isGeofencingEnabled &&
  (address.notValidForGeofencing === true ||
    address.lat == null ||
    address.lng == null)

const getContactDetailsViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string,
  isGeofencingEnabled: boolean
): DescriptionsItemType[] => {
  const addressesItems: DescriptionsItemType[] = resident.addresses.map(
    (address, index) => {
      const addressString = getAddressString(address)
      const invalidForGeofencing = isAddressInvalidForGeofencing(
        address,
        isGeofencingEnabled
      )
      return {
        key: `address-${index}-${address.street1}`,
        label: <Text>{t(supConstants[address.type])}</Text>,
        children: (
          <AddressCellWrapper>
            {invalidForGeofencing && (
              <Tooltip
                placement="top"
                title={
                  <Text>
                    {t(supConstants.ADDRESS_NOT_VALID_FOR_GEOFENCING_TOOLTIP)}
                  </Text>
                }
                overlayInnerStyle={{
                  width: '350px',
                  padding: '8px 16px'
                }}
              >
                <GeofencingWarningIcon />
              </Tooltip>
            )}
            <Text>{addressString}</Text>
          </AddressCellWrapper>
        )
      }
    }
  )
  const phonesItems: DescriptionsItemType[] = resident.phones.map(
    (phone, index) => ({
      key: `phone-${index}-${phone.number}`,
      label: <Text>{t(supConstants[`${phone.type}_NUMBER`])}</Text>,
      children: <Text>{phone.number}</Text>
    })
  )

  const items: DescriptionsItemType[] = [
    ...addressesItems,
    ...phonesItems,
    {
      key: 'preferredContactMethod',
      label: <Text>{t(supConstants.PREFERRED_CONTACT_METHOD)}</Text>,
      children: resident.preferredContactMethod && (
        <Text>{t(supConstants[resident.preferredContactMethod])}</Text>
      )
    },
    {
      key: 'email',
      label: <Text>{t(supConstants.EMAIL)}</Text>,
      children: resident.email && <Text>{resident.email}</Text>
    }
  ]

  const filteredValues = items.filter((item) => item.children)

  return filteredValues
}

const getCareInformationViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  if (resident.residentAllergies && resident.residentAllergies.length === 0) {
    return []
  }

  const hasNoKnownAllergies = resident.noKnownAllergies === true

  const allergiesItems: DescriptionsItemType[] = resident.residentAllergies.map(
    (allergy, index) => ({
      key: `allergy-${index}-${allergy.causativeAgent}`,
      label: <Text>{t(supConstants.ALLERGY_TYPE)}</Text>,
      children: (
        <ChildrenContainer>
          {!hasNoKnownAllergies && (
            <>
              <Text>{allergy.causativeAgent}</Text>
              <ExtraText level={2}>{allergy.typeOfReaction}</ExtraText>
              <ExtraText level={2}>{allergy.severityCode}</ExtraText>
              <ExtraText level={2}>{allergy.severityDescription}</ExtraText>
            </>
          )}
          {hasNoKnownAllergies && (
            <Text>{t(supConstants.NO_KNOWN_ALLERGIES)}</Text>
          )}
        </ChildrenContainer>
      )
    })
  )

  const filteredValues = allergiesItems.filter((item) => item.children)

  return filteredValues
}

const AddressCellWrapper = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const GeofencingWarningIcon = styled(ExclamationCircleOutlined)`
  color: ${(props) => props.theme.color.status.warning};
  font-size: 16px;
  flex-shrink: 0;
`
